// GymSalesJourneyCard.js — agent-facing widget on the call detail page.
// Mirrors the GymSales People screen (client profile + activity counters
// + full timeline) so consultants can see the caller's complete history
// beside the transcript. Uses REACT_APP_BACKEND_URL for API calls.
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  User, Phone, Mail, MapPin, Tag, AlertOctagon, Loader2, ChevronDown, ChevronUp,
  PhoneCall, MessageSquare, MessageCircle, FileText, Users, Calendar, Circle,
  CheckCircle2, ShieldOff, Ban,
} from 'lucide-react';
import CallButton from '../../components/CallButton';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const _fmtDate = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      year: 'numeric', month: 'short', day: 'numeric',
    });
  } catch { return iso; }
};

const _fmtDateTime = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString(undefined, {
      month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
    });
  } catch { return iso; }
};

const _timelineIcon = (kind) => {
  switch (kind) {
    case 'call':        return <PhoneCall className="w-3.5 h-3.5 text-blue-600" />;
    case 'sms':         return <MessageSquare className="w-3.5 h-3.5 text-purple-600" />;
    case 'email':       return <Mail className="w-3.5 h-3.5 text-emerald-600" />;
    case 'note':        return <FileText className="w-3.5 h-3.5 text-amber-600" />;
    case 'appointment': return <Calendar className="w-3.5 h-3.5 text-teal-600" />;
    case 'status':      return <Circle className="w-3.5 h-3.5 text-slate-600" />;
    default:            return <Circle className="w-3.5 h-3.5 text-slate-400" />;
  }
};

const Chip = ({ children, className = '' }) => (
  <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-semibold ${className}`}>{children}</span>
);

const OptOutBadges = ({ person }) => {
  const badges = [];
  if (person.do_not_call)       badges.push(['Do Not Call', 'bg-red-100 text-red-700', <Ban className="w-3 h-3" key="dnc" />]);
  if (person.sms_opted_out)     badges.push(['SMS Opt-out', 'bg-red-100 text-red-700', <ShieldOff className="w-3 h-3" key="sms" />]);
  if (person.email_opted_out)   badges.push(['Email Opt-out', 'bg-red-100 text-red-700', <ShieldOff className="w-3 h-3" key="em" />]);
  if (person.whatsapp_opted_out) badges.push(['WhatsApp Opt-out', 'bg-red-100 text-red-700', <ShieldOff className="w-3 h-3" key="wa" />]);
  if (person.email_suppressed)  badges.push(['Email Suppressed', 'bg-amber-100 text-amber-800', <AlertOctagon className="w-3 h-3" key="es" />]);
  if (!badges.length) return null;
  return (
    <div className="flex flex-wrap gap-1 mt-2" data-testid="gymsales-optouts">
      {badges.map(([label, klass, icon], i) => (
        <Chip key={i} className={klass}>{icon}{label}</Chip>
      ))}
    </div>
  );
};

const CounterCell = ({ icon, label, value }) => (
  <div className="flex items-center justify-between px-2 py-1.5 bg-white/60 border border-slate-200 rounded">
    <span className="text-slate-600 text-xs inline-flex items-center gap-1.5">{icon}{label}</span>
    <span className="text-slate-900 text-sm font-bold">{value ?? 0}</span>
  </div>
);

const GymSalesJourneyCard = ({ callId }) => {
  const [loading, setLoading] = useState(true);
  const [snap, setSnap] = useState(null);
  const [expanded, setExpanded] = useState(true);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const r = await axios.get(`${API}/calls/${callId}/gymsales-journey`);
      setSnap(r.data);
    } catch (e) {
      // 409 = disabled; hide entirely. 404 = call missing (parent renders own error).
      // Any other error, we simply hide — this is a read-only enrichment widget.
      if (e?.response?.status !== 409 && e?.response?.status !== 404) {
        setError(e?.response?.data?.detail || '');
      }
      setSnap(null);
    } finally {
      setLoading(false);
    }
  }, [callId]);

  useEffect(() => { if (callId) load(); }, [callId, load]);

  // Silent while loading — no flicker.
  if (loading || !snap) return null;

  // Phone not matched in GymSales — quiet inline notice, not alarming.
  if (!snap.matched) {
    return (
      <div
        className="rounded-xl border border-slate-200 bg-slate-50 p-3 text-xs text-slate-600 flex items-center gap-2"
        data-testid="gymsales-journey-nomatch"
      >
        <User className="w-4 h-4 shrink-0" />
        <span>
          No GymSales record found for this caller.
          {snap.reason ? <span className="text-slate-500"> ({snap.reason})</span> : null}
        </span>
      </div>
    );
  }

  // GymSales flags this caller BADLEAD / LOST — history pull stopped.
  if (snap.do_not_pursue) {
    const label = snap.flag === 'lost' ? 'lost deal' : 'bad lead';
    const p = snap.person || {};
    const name = [p.first_name, p.last_name].filter(Boolean).join(' ') || '(no name)';
    return (
      <div
        className="rounded-xl border-2 border-rose-300 bg-rose-50 p-4"
        data-testid="gymsales-journey-flagged"
      >
        <div className="flex items-center gap-2 flex-wrap">
          <Ban className="w-4 h-4 text-rose-700 shrink-0" />
          <span className="text-sm font-bold text-rose-900">
            {name} — expected {label} (GymSales)
          </span>
          {(snap.flag_tags || []).map((t) => (
            <Chip key={t} className="bg-rose-200 text-rose-800 uppercase">{t}</Chip>
          ))}
        </div>
        <p className="text-xs text-rose-800 mt-1.5">
          GymSales tags this caller as {snap.flag === 'lost' ? 'LOST' : 'BAD LEAD'} — history pull stopped and the call is treated as an expected {label}.
        </p>
      </div>
    );
  }

  const p = snap.person || {};
  const c = snap.counters || {};
  const displayName = [p.first_name, p.last_name].filter(Boolean).join(' ') || '(no name)';
  const bestPhone = p.phone_mobile || p.phone_home || p.phone_work;

  return (
    <div
      className="rounded-xl border-2 border-emerald-300 bg-white overflow-hidden"
      data-testid="gymsales-journey-card"
    >
      {/* Header — mimics GymSales green banner */}
      <div className="bg-gradient-to-r from-emerald-500 to-emerald-600 px-4 py-3 text-white">
        <button
          onClick={() => setExpanded((v) => !v)}
          className="w-full flex items-center justify-between text-left"
          data-testid="gymsales-journey-toggle"
        >
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-8 h-8 rounded-full bg-white/25 flex items-center justify-center shrink-0">
              <User className="w-4 h-4" />
            </div>
            <div className="min-w-0">
              <div className="font-bold text-base truncate">{displayName}</div>
              <div className="text-[11px] text-emerald-50 truncate">
                GymSales record{snap.site?.name ? ` · ${snap.site.name}` : ''} · {p.status || 'no status'} · {snap.assigned_consultant ? `assigned ${snap.assigned_consultant}` : 'unassigned'}
              </div>
            </div>
          </div>
          {expanded
            ? <ChevronUp className="w-5 h-5 text-white/80 shrink-0" />
            : <ChevronDown className="w-5 h-5 text-white/80 shrink-0" />}
        </button>
      </div>

      {expanded && (
        <div className="p-4 space-y-4">
          {/* Contact + tags */}
          <div className="grid md:grid-cols-2 gap-3 text-xs">
            <div className="space-y-1.5">
              {bestPhone && (
                <div className="flex items-center gap-2 text-slate-700">
                  <Phone className="w-3.5 h-3.5 text-slate-400" />
                  <span>{bestPhone}</span>
                  <CallButton destination={bestPhone} reason="gymsales_journey" context={{ gymsales_lead_id: p.id }} size="sm" label="Call" />
                </div>
              )}
              {p.email && (
                <div className="flex items-center gap-2 text-slate-700 min-w-0">
                  <Mail className="w-3.5 h-3.5 text-slate-400 shrink-0" />
                  <span className="truncate">{p.email}</span>
                </div>
              )}
              {(p.city || p.state) && (
                <div className="flex items-center gap-2 text-slate-700">
                  <MapPin className="w-3.5 h-3.5 text-slate-400" />
                  {[p.city, p.state, p.zip].filter(Boolean).join(', ')}
                </div>
              )}
              {p.source_name && (
                <div className="text-slate-600">
                  <span className="text-slate-400">Source: </span>{p.source_name}
                </div>
              )}
              {snap.site?.name && (
                <div className="flex items-center gap-2 text-slate-700" data-testid="gymsales-site-label">
                  <MapPin className="w-3.5 h-3.5 text-emerald-500" />
                  <span className="font-medium text-emerald-700">{snap.site.name}</span>
                  <span className="text-slate-400 text-[10px]">site {snap.site.company_id}</span>
                </div>
              )}
              {snap.most_recent_enquiry_at && (
                <div className="text-slate-600">
                  <span className="text-slate-400">Most recent enquiry: </span>{_fmtDate(snap.most_recent_enquiry_at)}
                </div>
              )}
            </div>

            <div className="space-y-1.5">
              {p.tags?.length > 0 && (
                <div className="flex items-start gap-2">
                  <Tag className="w-3.5 h-3.5 text-slate-400 mt-0.5" />
                  <div className="flex flex-wrap gap-1">
                    {p.tags.map((t) => (
                      <Chip key={t} className="bg-slate-100 text-slate-700">{t}</Chip>
                    ))}
                  </div>
                </div>
              )}
              {snap.objections_seen?.length > 0 && (
                <div className="flex items-start gap-2">
                  <AlertOctagon className="w-3.5 h-3.5 text-amber-500 mt-0.5" />
                  <div>
                    <div className="text-slate-500 text-[10px] uppercase tracking-wider">Objections seen</div>
                    <div className="flex flex-wrap gap-1 mt-0.5">
                      {snap.objections_seen.map((o) => (
                        <Chip key={o} className="bg-amber-100 text-amber-800">{o}</Chip>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          <OptOutBadges person={p} />

          {/* Counters */}
          <div>
            <h5 className="text-slate-500 text-[10px] uppercase tracking-widest font-semibold mb-1.5">Activity ({c.days_since_last_contact != null ? `last contact ${c.days_since_last_contact}d ago` : 'no dated activity'})</h5>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-1.5">
              <CounterCell icon={<PhoneCall className="w-3 h-3" />} label="Call Attempts" value={c.calls_total} />
              <CounterCell icon={<Users className="w-3 h-3" />} label="Spoke To" value={c.calls_answered} />
              <CounterCell icon={<MessageSquare className="w-3 h-3" />} label="SMS" value={c.sms_total} />
              <CounterCell icon={<Mail className="w-3 h-3" />} label="Emails" value={c.emails_total} />
              <CounterCell icon={<Calendar className="w-3 h-3" />} label="Appointments" value={c.appointments_total} />
              <CounterCell icon={<FileText className="w-3 h-3" />} label="Notes" value={c.notes_total} />
              <CounterCell icon={<MessageCircle className="w-3 h-3" />} label="Left Msg" value={c.calls_left_message} />
              <CounterCell icon={<CheckCircle2 className="w-3 h-3" />} label="Status Chgs" value={c.status_changes_total} />
            </div>
          </div>

          {/* Timeline */}
          {snap.timeline?.length > 0 && (
            <div>
              <h5 className="text-slate-500 text-[10px] uppercase tracking-widest font-semibold mb-1.5">History (newest first · {snap.timeline.length} events)</h5>
              <div className="max-h-96 overflow-y-auto space-y-1.5 pr-1" data-testid="gymsales-timeline">
                {snap.timeline.map((e, i) => (
                  <div key={i} className="flex gap-2 items-start bg-slate-50 border border-slate-200 rounded-lg px-2.5 py-1.5">
                    <div className="w-5 h-5 shrink-0 rounded-full bg-white border border-slate-200 flex items-center justify-center mt-0.5">
                      {_timelineIcon(e.kind)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-slate-800 text-xs font-semibold uppercase tracking-wide">{e.kind}</span>
                        {e.outcome && <Chip className="bg-blue-100 text-blue-800">{e.outcome}</Chip>}
                        <span className="text-slate-400 text-[10px] ml-auto">{_fmtDateTime(e.occurred_at)}</span>
                      </div>
                      {e.subject && <div className="text-slate-700 text-xs mt-0.5 truncate">{e.subject}</div>}
                      {e.body_excerpt && (
                        <div className="text-slate-500 text-[11px] mt-1 line-clamp-3 whitespace-pre-wrap">{e.body_excerpt}</div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="text-[10px] text-slate-400 pt-2 border-t border-slate-100 flex items-center justify-between">
            <span>Fetched from GymSales {_fmtDateTime(snap.fetched_at)}</span>
            <button
              onClick={load}
              className="text-emerald-600 hover:text-emerald-700 inline-flex items-center gap-1"
              data-testid="gymsales-journey-refresh"
              disabled={loading}
            >
              {loading ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Refresh'}
            </button>
          </div>

          {error && (
            <div className="mt-2 text-[11px] text-rose-700 bg-rose-50 border border-rose-200 rounded px-2 py-1">
              {error}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default GymSalesJourneyCard;
